import { useState, useCallback } from "react";

import { getUserCourseValidPeriod } from "../../api/positionValidPeriods";
import type { PositionValidPeriodResponse } from "../../shared/api-types/course";

export interface UserPeriod {
  /** Position-specific override from the API, null if no override */
  positionOverride: PositionValidPeriodResponse | null;
  /** Resolved months: positionOverride?.validPeriod ?? courseDefaultMonths ?? null (null when never expires) */
  resolvedMonths: number | null;
  /** True when this user's resolved period is "never expires" */
  isNeverExpires: boolean;
}

export interface PeriodsResult {
  /** Resolved months value if all users share the same period, null if divergent or unknown */
  resolvedMonths: number | null;
  /** Non-null when users have different periods: maps months → user count */
  divergentPeriods: Map<number, number> | null;
  /** True if all resolved months came from a position override (not the course default) */
  allFromPositionOverride: boolean;
  /** True when all resolved periods are "never expires" (validPeriod === 0 or courseDefault === 0) */
  allNeverExpires: boolean;
}

/** Sentinel key used in divergentPeriods to represent "never expires" users */
export const NEVER_EXPIRES_SENTINEL = 0;

export interface CoursePeriodTarget {
  courseId: number;
  isCustom: boolean;
  userId: number;
  courseDefaultMonths: number | null;
}

const resolveUserPeriod = (
  positionOverride: PositionValidPeriodResponse | null,
  courseDefaultMonths: number | null,
): UserPeriod => {
  // validPeriod === 0 means "never expires" — treat as null for calculation purposes
  const apiMonths = positionOverride?.validPeriod === 0 ? null : (positionOverride?.validPeriod ?? null);
  const defaultMonths = courseDefaultMonths === 0 ? null : courseDefaultMonths;
  const resolvedMonths = apiMonths ?? defaultMonths;
  const isNeverExpires = positionOverride?.validPeriod === 0 || (positionOverride === null && courseDefaultMonths === 0);
  return { positionOverride, resolvedMonths, isNeverExpires };
};

export function usePositionValidPeriods(
  crewId: number,
  courseId: number,
  isCustom: boolean,
  userIds: number[],
  courseDefaultMonths: number | null,
  courseTargets: CoursePeriodTarget[] = [],
) {
  const [userPeriods, setUserPeriods] = useState<Map<string, UserPeriod>>(new Map());
  const [fetching, setFetching] = useState(false);

  const fetchPeriods = useCallback(async () => {
    if (courseTargets.length === 0 && (!courseId || userIds.length === 0)) return;
    setFetching(true);
    try {
      const entries =
        courseTargets.length > 0
          ? await Promise.all(
              courseTargets.map(async (target) => {
                const r = await getUserCourseValidPeriod(crewId, target.userId, target.courseId, target.isCustom);
                const key = `${target.isCustom ? "custom" : "standard"}:${target.courseId}`;
                return [key, resolveUserPeriod(r ?? null, target.courseDefaultMonths)] as const;
              }),
            )
          : await Promise.all(
              userIds.map(async (id) => {
                const r = await getUserCourseValidPeriod(crewId, id, courseId, isCustom);
                return [String(id), resolveUserPeriod(r ?? null, courseDefaultMonths)] as const;
              }),
            );
      setUserPeriods(new Map(entries));
    } finally {
      setFetching(false);
    }
  }, [crewId, courseId, isCustom, userIds, courseDefaultMonths, courseTargets]);

  const getResult = useCallback((): PeriodsResult => {
    if (userPeriods.size === 0) {
      return { resolvedMonths: null, divergentPeriods: null, allFromPositionOverride: false, allNeverExpires: false };
    }

    const periods = Array.from(userPeriods.values());
    const allNeverExpires = periods.every((p) => p.isNeverExpires);
    if (allNeverExpires) {
      return { resolvedMonths: null, divergentPeriods: null, allFromPositionOverride: false, allNeverExpires: true };
    }

    // Use NEVER_EXPIRES_SENTINEL (0) in monthsCounts so mixed never-expires + real-period
    // users are detected as divergent rather than silently dropping the never-expires users.
    const monthsCounts = new Map<number, number>();
    for (const { resolvedMonths, isNeverExpires } of periods) {
      const key = isNeverExpires ? NEVER_EXPIRES_SENTINEL : resolvedMonths;
      if (key !== null) {
        monthsCounts.set(key, (monthsCounts.get(key) ?? 0) + 1);
      }
    }

    if (monthsCounts.size === 0) {
      return { resolvedMonths: null, divergentPeriods: null, allFromPositionOverride: false, allNeverExpires: false };
    }

    const allFromPositionOverride = periods.every((p) => p.positionOverride?.validPeriod != null);

    if (monthsCounts.size === 1) {
      const onlyKey = Array.from(monthsCounts.keys())[0];
      // If the only bucket is the never-expires sentinel, all remaining (non-allNeverExpires) users somehow
      // resolved to never-expires — treat as allNeverExpires.
      if (onlyKey === NEVER_EXPIRES_SENTINEL) {
        return { resolvedMonths: null, divergentPeriods: null, allFromPositionOverride: false, allNeverExpires: true };
      }
      return {
        resolvedMonths: onlyKey,
        divergentPeriods: null,
        allFromPositionOverride,
        allNeverExpires: false,
      };
    }
    return {
      resolvedMonths: null,
      divergentPeriods: monthsCounts,
      allFromPositionOverride,
      allNeverExpires: false,
    };
  }, [userPeriods]);

  return { fetchPeriods, fetching, getResult };
}
